<template>
    <hox-modal data-testid="add-section-modal" @close="close">
        <hox-loading-layer v-if="isLoadingAddingSection" />
        <template #header>Add section</template>
        <CampaignSectionForm
            @hasValidationErrorsUpdate="setHasValidationErrors"
            @formDataUpdate="setFormData"
            @submit="addSection"
        />
        <div v-if="hasErrorAddingSection" class="add-campaign-section-modal__unexpected-error-wrapper">
            <Alert banner type="error">
                <template #desc>
                    <div v-if="mutationErrors">
                        <p v-for="error in mutationErrors" :key="error" class="error-wrapper">Error: {{ error }}</p>
                    </div>

                    <p v-else>
                        There was an unexpected error and the section could not be added. Please try again in a few
                        moments.
                    </p>
                </template>
            </Alert>
        </div>
        <template #footer>
            <Button
                :disabled="hasValidationErrors || hasErrorAddingSection"
                type="primary"
                data-testid="add-section-modal__add-button"
                @click="addSection"
            >
                Add
            </Button>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { ApolloError } from "@apollo/client/core";

import CampaignSectionForm from "@/components/CampaignSectionForm.vue";
import addCampaignSectionMutation from "@/apollo/mutations/AddCampaignSection.gql";

export default defineComponent({
    components: {
        CampaignSectionForm
    },

    props: {
        clientId: {
            required: true,
            type: String
        }
    },

    emits: ["close", "sectionAdded"],

    data() {
        return {
            hasErrorAddingSection: false,
            hasValidationErrors: false,
            isLoadingAddingSection: false,
            formData: {} as { name: string },
            mutationErrors: [] as string[]
        };
    },

    watch: {
        "formData.name": function () {
            this.mutationErrors = [];
            this.hasErrorAddingSection = false;
        }
    },

    methods: {
        async addSection() {
            if (this.hasValidationErrors) {
                return;
            }

            this.hasErrorAddingSection = false;
            this.isLoadingAddingSection = true;

            try {
                await this.$apollo.mutate({
                    mutation: addCampaignSectionMutation,
                    variables: {
                        clientId: this.clientId,
                        name: this.formData.name
                    }
                });

                this.$emit("sectionAdded");
                this.close();
            } catch (error) {
                const graphQLErrors = (error as ApolloError)?.graphQLErrors || [];
                this.mutationErrors = graphQLErrors.map(({ message }) => message);
                this.hasErrorAddingSection = true;
            } finally {
                this.isLoadingAddingSection = false;
            }
        },

        close() {
            this.$emit("close");
        },

        setFormData(formData: { name: string }) {
            this.formData = formData;
        },

        setHasValidationErrors(hasValidationErrors: boolean) {
            this.hasValidationErrors = hasValidationErrors;
        }
    }
});
</script>

<style scoped lang="scss">
@use "@style/variables" as *;

.add-campaign-section-modal__unexpected-error-wrapper {
    padding: $spacing 0 0 0;
}
</style>
